import { createContext, useCallback, useContext, useMemo, useState } from "react";

const STORAGE_KEY = "lead3a.admin.workspaceSelectionMode.v1";
const WorkspaceSelectionModeContext = createContext(null);

function readState() {
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
}

function persist(value) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch {}
}

export function WorkspaceSelectionModeProvider({ children }) {
  const [state, setState] = useState(readState);

  const update = useCallback((updater) => {
    setState((current) => {
      const next = updater(current);
      persist(next);
      return next;
    });
  }, []);

  const registerSectionState = useCallback((section, view) => {
    update((current) => current.activeGroup === section
      ? { ...current, activeView: view }
      : { ...current, sectionStates: { ...current.sectionStates, [section]: view } });
  }, [update]);

  const enterMode = useCallback((group) => {
    update((current) => ({
      ...current,
      activeGroup: group,
      restoreState: current.sectionStates?.[group] || null,
      activeView: current.sectionStates?.[group] || null,
    }));
  }, [update]);

  const exitMode = useCallback(() => {
    update((current) => ({ ...current, activeGroup: null, restoreState: null, activeView: null }));
  }, [update]);

  const value = useMemo(() => ({
    activeGroup: state.activeGroup || null,
    restoreState: state.restoreState || null,
    activeView: state.activeView || null,
    enterMode,
    exitMode,
    registerSectionState,
  }), [enterMode, exitMode, registerSectionState, state.activeGroup, state.activeView, state.restoreState]);

  return <WorkspaceSelectionModeContext.Provider value={value}>{children}</WorkspaceSelectionModeContext.Provider>;
}

export function useWorkspaceSelectionMode() {
  const context = useContext(WorkspaceSelectionModeContext);
  if (!context) throw new Error("useWorkspaceSelectionMode must be used inside WorkspaceSelectionModeProvider");
  return context;
}
